import { Color3, type Mesh, MeshBuilder, StandardMaterial, TransformNode, Vector3 } from "@babylonjs/core";
import { HeroId, Slot, hv } from "@rivalwatch/shared";
import { CK, FX, PK, V } from "../../../../shared/src/heroes/roster/squawkeye.ts";
import { baked } from "../../audio/bake.ts";
import { bell, click, fm, later, mallet, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero, ConstructFrame } from "../types.ts";

type Gear = { limbTop: Mesh; limbBottom: Mesh; stringTop: Mesh; stringBottom: Mesh; quiver: Mesh; arrow: Mesh; eye: Mesh };

const bowFire: Cue = baked("squawkeye:bow-fire", () => trim(9.5, (o) => {
  click(o, 0.34, 2600, 0.006);
  tone(o, { f: 170, f1: 95, glide: 0.08, gain: 0.18, d: 0.16 });
  whoosh(o, 0.58, 900, 4200, 0.18);
  fm(later(o, 0.025), { f: 860, ratio: 1.98, index: 1.8, index1: 0.1, gain: 0.08, d: 0.18 });
  return 0.42;
}), { variants: 5, jitter: 0.018 });
const blastCue: Cue = trim(6.5, (o) => {
  for (let i = 0; i < 3; i++) {
    click(later(o, i * 0.045), 0.24, 3300, 0.004);
    whoosh(later(o, i * 0.045), 0.34, 700, 2600, 0.14);
  }
  thump(later(o, 0.1), 0.44, 150, 60, 0.12);
  return 0.55;
});
const slashCue: Cue = trim(5.2, (o) => {
  whoosh(o, 0.88, 500, 3400, 0.2);
  fm(later(o, 0.08), { f: 1050, ratio: 2.1, index: 3, index1: 0, gain: 0.13, d: 0.22 });
  thump(later(o, 0.1), 0.4, 170, 70, 0.1);
  return 0.48;
});
const hyperCue: Cue = trim(10.2, (o) => {
  click(o, 0.4, 6000, 0.003);
  whoosh(o, 0.9, 1600, 6200, 0.22, 0, 1.8);
  tone(later(o, 0.02), { f: 1800, f1: 3200, glide: 0.18, gain: 0.08, d: 0.2 });
  return 0.5;
});
const leapCue: Cue = trim(10, (o) => {
  whoosh(o, 0.62, 500, 1900, 0.22);
  thump(o, 0.26, 180, 80, 0.08);
  return 0.35;
});
const sightCue: Cue = trim(5.3, (o) => {
  bell(o, 1174.6, 0.5, 0.8);
  bell(later(o, 0.07), 1567.9, 0.5, 0.7);
  noise(o, { color: "pink", type: "bandpass", f: 1300, f1: 3200, gain: 0.28, a: 0.08, d: 0.38 });
  return 0.95;
});
const arrowImpact: Cue = trim(7, (o) => {
  click(o, 0.28, 4200, 0.004);
  fm(o, { f: 1800, ratio: 1.73, index: 1.4, index1: 0, gain: 0.08, d: 0.12 });
  return 0.2;
});
const blastBoom = (radius: number): Cue => trim(8, (o) => {
  thump(o, 0.85, 120, 42, 0.28);
  noise(o, { color: "pink", type: "lowpass", f: 2200, f1: 300, gain: 0.55, d: 0.35 + radius * 0.04 });
  return 0.75;
});
const theme = {
  bpm: 132,
  octave: 0,
  voice: mallet,
  double: (o, f, d, v) => void bell(o, f * 2, d, v * 0.5),
  notes: [[0, 0.5], [7, 0.5], [9, 0.5], [6, 0.5], [7, 1.5]],
} satisfies ClientHero["audio"]["theme"];

const icons = {
  primary: "M4 20L20 4M7 17l3 3M14 10l6 1",
  burst: "M5 18l7-12 7 12M8 15h8M10 11h4",
  slash: "M5 18c5-7 9-10 14-12M7 6l11 11",
  hyper: "M3 12h18M15 6l6 6-6 6M5 8l4 4-4 4",
  leap: "M5 17c5-1 8-4 10-10M12 4l3 3 4-1",
  sight: "M4 12s3-6 8-6 8 6-3 6-8 6-8-6M12 9v6M9 12h6",
};

function arrowMesh(scene: import("@babylonjs/core").Scene, name: string, color: string, length = 0.95) {
  const root = new TransformNode(name, scene);
  const mat = new StandardMaterial(`${name}-mat`, scene);
  mat.emissiveColor = Color3.FromHexString(color);
  mat.disableLighting = true;
  const shaft = MeshBuilder.CreateCylinder(`${name}-shaft`, { diameter: 0.035, height: length, tessellation: 6 }, scene);
  shaft.material = mat;
  shaft.parent = root;
  shaft.rotation.x = Math.PI / 2;
  const head = MeshBuilder.CreateCylinder(`${name}-head`, { diameterTop: 0, diameterBottom: 0.13, height: 0.18, tessellation: 4 }, scene);
  head.material = mat;
  head.parent = root;
  head.rotation.x = -Math.PI / 2;
  head.position.z = -length * 0.5;
  return root;
}

const hero: ClientHero<Gear> = {
  id: HeroId.Squawkeye,
  shape: { head: 0.95, torso: 0.95, arms: 1.05, legs: 1.05 },
  gear(g) {
    const { scene, add, rig, toon, glow, R } = g;
    const limbTop = add(MeshBuilder.CreateCylinder("bow-limb-top", { diameter: 0.055, height: 0.72, tessellation: 8 }, scene), rig.handL, toon("#20122d"));
    limbTop.position.set(0, 0.34, -0.14);
    limbTop.rotation.z = 0.18;
    const limbBottom = add(MeshBuilder.CreateCylinder("bow-limb-bottom", { diameter: 0.055, height: 0.72, tessellation: 8 }, scene), rig.handL, toon("#20122d"));
    limbBottom.position.set(0, -0.34, -0.14);
    limbBottom.rotation.z = -0.18;
    const stringTop = add(MeshBuilder.CreateBox("bow-string-top", { width: 0.02, height: 0.66, depth: 0.02 }, scene), rig.handL, glow("#d7a7ff", 0.7));
    stringTop.position.set(0, 0.34, 0.08);
    const stringBottom = add(MeshBuilder.CreateBox("bow-string-bottom", { width: 0.02, height: 0.66, depth: 0.02 }, scene), rig.handL, glow("#d7a7ff", 0.7));
    stringBottom.position.set(0, -0.34, 0.08);
    const quiver = add(MeshBuilder.CreateCylinder("quiver", { diameter: R * 0.42, height: 1.0, tessellation: 10 }, scene), rig.torso, toon("#120d19"));
    quiver.rotation.x = 0.5;
    quiver.rotation.z = -0.22;
    quiver.position.set(R * 0.72, 0.05, R * 0.7);
    const arrow = add(MeshBuilder.CreateCylinder("held-arrow", { diameter: 0.04, height: 0.95, tessellation: 6 }, scene), rig.handR, glow("#d7a7ff"));
    arrow.rotation.x = Math.PI / 2;
    arrow.position.z = -0.42;
    const eye = add(MeshBuilder.CreateTorus("hindsight", { diameter: 0.5, thickness: 0.018, tessellation: 24 }, scene), rig.head, glow("#d7a7ff", 0.55));
    eye.rotation.x = Math.PI / 2;
    eye.position.set(0, 0.08, -0.24);
    return { limbTop, limbBottom, stringTop, stringBottom, quiver, arrow, eye };
  },
  animate(gear, f) {
    const charge = Math.max(f.v[V.draw], f.v[V.focus]);
    gear.stringTop.position.z = 0.08 + charge * 0.24;
    gear.stringBottom.position.z = 0.08 + charge * 0.24;
    gear.stringTop.rotation.x = -charge * 0.22;
    gear.stringBottom.rotation.x = charge * 0.22;
    gear.arrow.position.z = -0.42 + charge * 0.24;
    gear.arrow.scaling.z = 1 + charge * 0.25;
    gear.eye.setEnabled(f.ultActive);
    gear.eye.rotation.z += f.dt * 6;
    if (f.action === 3 || f.action === 4) return "attack-melee-right";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: "Draw", value: hv(c, eid, V.draw) / 0.9, color: "#d7a7ff" },
      { label: "Focus", value: hv(c, eid, V.focus) / 0.9, color: "#8e44ad" },
    ];
  },
  icons: { [Slot.Primary]: icons.primary, [Slot.Secondary]: icons.burst, [Slot.Ability1]: icons.slash, [Slot.Ability2]: icons.hyper, [Slot.Ability3]: icons.leap, [Slot.Ultimate]: icons.sight, passive: "M6 18c5-12 7-12 12 0" },
  constructs: {
    [CK.Afterimage](scene, f: ConstructFrame) {
      const root = MeshBuilder.CreateCapsule("afterimage", { radius: 0.32, height: 1.75, tessellation: 8, subdivisions: 1 }, scene);
      const mat = new StandardMaterial("afterimage-mat", scene);
      mat.emissiveColor = Color3.FromHexString("#d7a7ff");
      mat.alpha = f.rel === "foe" ? 0.42 : 0;
      mat.disableLighting = true;
      root.material = mat;
      return {
        gear: root,
        update(mesh: Mesh, frame) {
          mesh.setEnabled(frame.rel === "foe");
          mesh.position.set(frame.pos.x, frame.pos.y + 0.9, frame.pos.z);
          mesh.rotation.y = frame.yaw;
          mesh.visibility = 0.2 + 0.45 * frame.hpFrac + Math.sin(frame.ticks * 0.12) * 0.06;
        },
        dispose: () => root.dispose(),
      };
    },
  },
  projectiles: {
    [PK.Piercing](scene) {
      const m = arrowMesh(scene, "piercing-arrow", "#d7a7ff", 1.0);
      return {
        gear: m,
        update(mesh: TransformNode, frame) {
          mesh.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          mesh.lookAt(mesh.position.add(new Vector3(frame.dir.x, frame.dir.y, frame.dir.z)));
        },
        dispose: () => m.dispose(),
      };
    },
    [PK.Blast](scene) {
      const m = arrowMesh(scene, "blast-arrow", "#ffb347", 0.8);
      return { gear: m, update: (mesh: TransformNode, frame) => (mesh.position.set(frame.pos.x, frame.pos.y, frame.pos.z), mesh.lookAt(mesh.position.add(new Vector3(frame.dir.x, frame.dir.y, frame.dir.z)))), dispose: () => m.dispose() };
    },
    [PK.Hyper](scene) {
      const m = arrowMesh(scene, "hyper-arrow", "#8ee8ff", 1.25);
      return { gear: m, update: (mesh: TransformNode, frame) => (mesh.position.set(frame.pos.x, frame.pos.y, frame.pos.z), mesh.lookAt(mesh.position.add(new Vector3(frame.dir.x, frame.dir.y, frame.dir.z)))), dispose: () => m.dispose() };
    },
  },
  audio: {
    weight: "mid",
    fire: () => ({ key: "squawkeye:bow", cue: bowFire }),
    ability: { [Slot.Secondary]: blastCue, [Slot.Ability1]: slashCue, [Slot.Ability2]: hyperCue, [Slot.Ability3]: leapCue, [Slot.Ultimate]: sightCue },
    impacts: { [FX.Arrow]: arrowImpact, [FX.Hyper]: arrowImpact },
    explosions: { [FX.Blast]: blastBoom, [FX.Slash]: blastBoom },
    theme,
    themeTrim: { heroic: 1.2, hostile: -6.1, tease: 4.7 },
  },
};

export default hero;
